import {
  Button,
  Chip,
  column,
  Css,
  dateColumn,
  emptyCell,
  GridDataRow,
  GridTable,
  simpleHeader,
  Tooltip,
} from "@homebound/beam";
import { chipCell, dateCell } from "src/components";
import { StatusIndicator } from "src/components/StatusIndicator";
import {
  BidPackageDetailPage_OverlappingAwardFragment,
  BidPackageDetailPage_OverlappingBidPackageFragment,
  PageBidPackageDetailFragment,
} from "src/routes/bids/BidPackageDetailPage.generated";
import { createBidPackagesDetailUrl } from "src/RouteUrls";
import { FormattedBidName } from "../BidPackagesPage";

type OverlappingBidPackagesTabProps = { bidPackage: PageBidPackageDetailFragment };

export function OverlappingBidPackagesTab({ bidPackage }: OverlappingBidPackagesTabProps) {
  const rows: GridDataRow<Row>[] = createRows(bidPackage);
  return <GridTable columns={columns} rows={rows} />;
}

function createRows(bidPackage: PageBidPackageDetailFragment): GridDataRow<Row>[] {
  return [
    simpleHeader,
    ...bidPackage.overlappingBidPackages.map((bp) => {
      return {
        kind: "bidPackage" as const,
        data: bp,
        id: bp.id,
        children: bp.latestAwards.map((award) => ({ kind: "award" as const, id: award.id, data: award })),
      };
    }),
  ];
}

type HeaderRow = { kind: "header"; data: undefined };

type BidPackageRow = { kind: "bidPackage"; data: BidPackageDetailPage_OverlappingBidPackageFragment };

type AwardRow = { kind: "award"; data: BidPackageDetailPage_OverlappingAwardFragment };

type Row = HeaderRow | BidPackageRow | AwardRow;

const columns = [
  column<Row>({
    header: "Bid Package",
    bidPackage: ({ latestVersion, bidPackageGroup, id, costCodesName, isPlanBasedV2 }) => (
      <Button
        variant="text"
        label={
          <FormattedBidName costCodesName={costCodesName} latestVersion={latestVersion} isPlanBasedV2={isPlanBasedV2} />
        }
        onClick={createBidPackagesDetailUrl(bidPackageGroup!.id, id)}
      />
    ),
    award: emptyCell,
  }),
  column<Row>({ header: "Cost Type", bidPackage: ({ costType }) => costType.name, award: emptyCell }),
  column<Row>({
    header: "Status",
    bidPackage: ({ status }) => (
      <div css={Css.df.aic.$}>
        <StatusIndicator status={status.code} type="BidPackage" />
        <span css={Css.ml1.$}>{status.name}</span>
      </div>
    ),
    award: emptyCell,
  }),
  column<Row>({
    header: "Hierarchy",
    bidPackage: ({ projects }) => chipCell(projects.map((p) => p.name)),
    award: emptyCell,
  }),
  column<Row>({
    header: "Awarded Cost Code",
    bidPackage: emptyCell,
    award: ({ costCode }) => (
      <Tooltip title={costCode.name} placement="top">
        <Chip text={costCode.number} xss={Css.mt0.$} compact />
      </Tooltip>
    ),
  }),
  column<Row>({
    header: "Primary Awardee",
    bidPackage: emptyCell,
    award: ({ primaryRevision }) => primaryRevision?.bidContract.tradePartner?.name,
  }),
  column<Row>({
    header: "Secondary Awardee",
    bidPackage: emptyCell,
    award: ({ secondaryRevision }) => secondaryRevision?.bidContract.tradePartner?.name,
  }),
  dateColumn<Row>({
    header: "Award Date",
    bidPackage: emptyCell,
    /*
      When we create a new revision that is updating the cost for a bid package awarded trade, we update the award row for that new revision.
      That would rerun the award process and send cost back to the product offering.
      So we should look at the updatedAt to see when that last time that award process could have been run
    */
    award: ({ updatedAt }) => dateCell(updatedAt),
  }),
];
